import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { IRegEdMetadataForm } from './types';

export const REGED_BROKER_DEALER_SCHEMA_QUERY_KEY = 'regEdBrokerDealerSchema';

/**
 * A firm's imported questionnaire, read without operating as an account under it. Fields carry no
 * `minimumDate` on this path — the review-date minimum is measured from the advisor's own calendar
 * day, and a preview has no advisor.
 */
export const useRegEdBrokerDealerSchemaQuery = (
	brokerDealerId: string | null | undefined,
	format: string | null | undefined
) => {
	const userSession = useUserSession();
	return useQuery({
		enabled: !!brokerDealerId && !!format,
		queryKey: [REGED_BROKER_DEALER_SCHEMA_QUERY_KEY, brokerDealerId ?? null, format ?? null],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IRegEdMetadataForm>({
				path: `admin/compliance/regEd/brokerDealers/${encodeURIComponent(
					brokerDealerId!
				)}/metadataSchema?format=${encodeURIComponent(format!)}`,
				method: 'GET',
				// A format in the firm's catalog with nothing imported for it yet answers 404. That is a
				// state the preview renders, not an error to raise.
				throwOn404: false,
			}),
		meta: {
			toastErrorMessage: 'Failed to load the schema for this format.',
		},
	});
};

export const invalidateRegEdBrokerDealerSchema = (brokerDealerId?: string) => {
	reactQueryClient.invalidateQueries({
		queryKey: brokerDealerId
			? [REGED_BROKER_DEALER_SCHEMA_QUERY_KEY, brokerDealerId]
			: [REGED_BROKER_DEALER_SCHEMA_QUERY_KEY],
	});
};
